import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ExternalLink, Folder, ArrowUpRight, X, Play, Eye } from 'lucide-react';
import { Link } from 'react-router-dom';
import { projects as mockProjects } from '../data/mock';
import { useTheme } from '../context/ThemeContext';
import MouseFollower from '../components/MouseFollower';

const BACKEND_URL = '';

const ProjectsPage = () => {
  const { themeColor, currentButtonShape } = useTheme();
  const [filter, setFilter] = useState('all');
  const [selectedProject, setSelectedProject] = useState(null);
  const [hoveredProject, setHoveredProject] = useState(null);
  const [projects, setProjects] = useState(mockProjects);

  useEffect(() => {
    fetch(`${BACKEND_URL}/api/admin/public/projects`, { cache: 'no-store' })
      .then(r => r.json())
      .then(data => { if (data.projects?.length > 0) setProjects(data.projects); })
      .catch(() => {});
  }, []);

  const categories = ['all', ...new Set(projects.map((p) => p.category))];
  const filteredProjects = filter === 'all'
    ? projects
    : projects.filter((p) => p.category === filter);

  // Check if project is a video
  const isVideoProject = (project) => {
    return project.liveUrl?.includes('youtube') || project.liveUrl?.includes('vimeo');
  };

  // Get YouTube thumbnail
  const getYouTubeThumbnail = (url) => {
    const match = url?.match(/(?:youtube\.com\/watch\?v=|youtu\.be\/)([^&]+)/);
    if (match) {
      return `https://img.youtube.com/vi/${match[1]}/maxresdefault.jpg`;
    }
    return null;
  };

  return (
    <div className="min-h-screen pt-24 pb-16 relative">
      <MouseFollower />
      
      {/* Background */}
      <div
        className="fixed inset-0 opacity-[0.02] pointer-events-none"
        style={{
          backgroundImage: `radial-gradient(${themeColor.primary} 1px, transparent 1px)`,
          backgroundSize: '40px 40px',
        }}
      />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        {/* Header */}
        <motion.div
          className="text-center mb-12"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
        >
          <motion.div
            className="inline-flex items-center gap-2 px-4 py-2 rounded-full mb-6 text-sm font-medium"
            style={{ backgroundColor: `rgba(${themeColor.rgb}, 0.1)`, color: themeColor.primary }}
          >
            <Folder size={16} />
            Portföy
          </motion.div>
          <h1 className="text-4xl md:text-6xl font-bold text-white mb-6">
            Projelerim
          </h1>
          <p className="text-zinc-400 text-lg max-w-2xl mx-auto mb-8">
            Grafik tasarım ve yazılım geliştirme alanlarındaki çalışmalarım
          </p>

          {/* Browse Button */}
          <Link
            to="/repositories"
            className="inline-flex items-center gap-2 px-6 py-3 rounded-xl font-medium text-white bg-zinc-800/50 hover:bg-zinc-700/50 transition-all border border-zinc-700/50 mb-8"
          >
            <Eye size={18} />
            <span>GitHub Repolarına Göz At</span>
            <ArrowUpRight size={16} />
          </Link>
        </motion.div>

        {/* Filter Tabs */}
        <motion.div
          className="flex flex-wrap justify-center gap-3 mb-12"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
        >
          {categories.map((cat) => (
            <motion.button
              key={cat}
              onClick={() => setFilter(cat)}
              className={`px-5 py-2.5 rounded-2xl text-sm font-medium transition-all ${
                filter === cat
                  ? 'text-black btn-glow'
                  : 'text-zinc-400 glass hover:text-white'
              }`}
              style={filter === cat ? { backgroundColor: themeColor.primary } : {}}
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
            >
              {cat === 'all' ? 'Tümü' : cat}
            </motion.button>
          ))}
        </motion.div>

        {/* Projects Grid */}
        <motion.div className="grid md:grid-cols-2 gap-6" layout>
          <AnimatePresence mode="popLayout">
            {filteredProjects.map((project, index) => (
              <motion.div
                key={project.id}
                layout
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0.9 }}
                transition={{ delay: index * 0.1 }}
                className="group relative rounded-3xl overflow-hidden glass glass-hover cursor-pointer"
                onClick={() => setSelectedProject(project)}
                onMouseEnter={() => setHoveredProject(project.id)}
                onMouseLeave={() => setHoveredProject(null)}
                whileHover={{ y: -5 }}
              >
                {/* Image */}
                <div className="relative aspect-video overflow-hidden">
                  <motion.img
                    src={isVideoProject(project) && getYouTubeThumbnail(project.liveUrl) 
                      ? getYouTubeThumbnail(project.liveUrl) 
                      : project.image}
                    alt={project.title}
                    className="w-full h-full object-cover"
                    whileHover={{ scale: 1.1 }}
                    transition={{ duration: 0.5 }}
                  />
                  
                  {/* Video Preview Indicator */}
                  {isVideoProject(project) && (
                    <motion.div
                      className="absolute inset-0 flex items-center justify-center"
                      initial={{ opacity: 0 }}
                      animate={{ opacity: hoveredProject === project.id ? 1 : 0 }}
                    >
                      <motion.div
                        className="w-16 h-16 rounded-full bg-white/20 backdrop-blur-sm flex items-center justify-center"
                        animate={{ scale: hoveredProject === project.id ? [1, 1.1, 1] : 1 }}
                        transition={{ duration: 1, repeat: Infinity }}
                      >
                        <Play size={28} className="text-white ml-1" fill="white" />
                      </motion.div>
                    </motion.div>
                  )}
                  
                  {/* Overlay */}
                  <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/50 to-transparent" />

                  {/* Video Badge */}
                  {isVideoProject(project) && (
                    <div className="absolute top-4 right-4 px-3 py-1.5 rounded-full text-xs font-medium bg-red-500/90 text-white flex items-center gap-1">
                      <Play size={12} fill="white" />
                      Video
                    </div>
                  )}

                  {/* Featured Badge */}
                  {project.featured && !isVideoProject(project) && (
                    <div
                      className="absolute top-4 right-4 px-3 py-1.5 rounded-full text-xs font-medium"
                      style={{ backgroundColor: themeColor.primary, color: '#000' }}
                    >
                      Öne Çıkan
                    </div>
                  )}

                  {/* Category */}
                  <div
                    className="absolute top-4 left-4 px-3 py-1.5 rounded-full text-xs font-medium backdrop-blur-sm"
                    style={{ backgroundColor: `rgba(${themeColor.rgb}, 0.2)`, color: themeColor.primary }}
                  >
                    {project.category}
                  </div>

                  {/* Content */}
                  <div className="absolute bottom-0 left-0 right-0 p-6">
                    <h3 className="text-xl md:text-2xl font-bold text-white mb-2">
                      {project.title}
                    </h3>
                    <p className="text-zinc-400 text-sm line-clamp-2 mb-4">
                      {project.description}
                    </p>

                    {/* Tags + View Button */}
                    <div className="flex items-center justify-between gap-3">
                      <div className="flex flex-wrap gap-2">
                        {project.tags.slice(0, 3).map((tag) => (
                          <span
                            key={tag}
                            className="px-2.5 py-1 rounded-lg text-xs bg-zinc-800/80 text-zinc-400"
                          >
                            {tag}
                          </span>
                        ))}
                        {project.tags.length > 3 && (
                          <span className="px-2.5 py-1 rounded-lg text-xs bg-zinc-800/80 text-zinc-400">
                            +{project.tags.length - 3}
                          </span>
                        )}
                      </div>
                      {project.liveUrl && (
                        <motion.a
                          href={project.liveUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                          onClick={(e) => e.stopPropagation()}
                          className="flex-shrink-0 flex items-center gap-1.5 px-4 py-2 text-sm font-medium text-black transition-all btn-glow"
                          style={{ backgroundColor: themeColor.primary, borderRadius: currentButtonShape.radius }}
                          whileHover={{ scale: 1.05 }}
                          whileTap={{ scale: 0.95 }}
                          data-testid={`project-view-btn-${project.id}`}
                        >
                          {isVideoProject(project) ? 'İzle' : 'Projeye Bak'}
                          <ExternalLink size={14} />
                        </motion.a>
                      )}
                    </div>
                  </div>
                </div>
              </motion.div>
            ))}
          </AnimatePresence>
        </motion.div>
      </div>

      {/* Project Modal */}
      <AnimatePresence>
        {selectedProject && (
          <>
            <motion.div
              className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={() => setSelectedProject(null)}
            />
            <motion.div
              className="fixed inset-4 md:inset-10 lg:inset-20 bg-zinc-900 rounded-3xl z-50 overflow-hidden"
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.9 }}
            >
              <button
                onClick={() => setSelectedProject(null)}
                className="absolute top-4 right-4 p-2 rounded-xl bg-zinc-800 text-white hover:bg-zinc-700 z-10"
              >
                <X size={24} />
              </button>

              <div className="h-full overflow-y-auto">
                <div className="relative aspect-video">
                  {isVideoProject(selectedProject) ? (
                    <iframe
                      src={selectedProject.liveUrl.replace('watch?v=', 'embed/').replace('youtu.be/', 'youtube.com/embed/')}
                      className="w-full h-full"
                      allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                      allowFullScreen
                      title={selectedProject.title}
                    />
                  ) : (
                    <>
                      <img
                        src={selectedProject.image}
                        alt={selectedProject.title}
                        className="w-full h-full object-cover"
                      />
                      <div className="absolute inset-0 bg-gradient-to-t from-zinc-900 to-transparent" />
                    </>
                  )}
                </div>

                <div className="p-8 -mt-20 relative">
                  <div
                    className="inline-block px-3 py-1.5 rounded-full text-xs font-medium mb-4"
                    style={{ backgroundColor: `rgba(${themeColor.rgb}, 0.2)`, color: themeColor.primary }}
                  >
                    {selectedProject.category}
                  </div>

                  <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
                    {selectedProject.title}
                  </h2>

                  <p className="text-zinc-400 text-lg mb-6">
                    {selectedProject.description}
                  </p>

                  <div className="flex flex-wrap gap-2 mb-8">
                    {selectedProject.tags.map((tag) => (
                      <span
                        key={tag}
                        className="px-3 py-1.5 rounded-lg text-sm bg-zinc-800 text-zinc-300"
                      >
                        {tag}
                      </span>
                    ))}
                  </div>

                  {selectedProject.liveUrl && (
                    <motion.a
                      href={selectedProject.liveUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-2 px-6 py-3 rounded-xl font-medium text-black"
                      style={{ backgroundColor: themeColor.primary }}
                      whileHover={{ scale: 1.05 }}
                      whileTap={{ scale: 0.95 }}
                    >
                      <span>{isVideoProject(selectedProject) ? 'YouTube\'da İzle' : 'Projeyi Görüntüle'}</span>
                      <ExternalLink size={18} />
                    </motion.a>
                  )}
                </div>
              </div>
            </motion.div>
          </>
        )}
      </AnimatePresence>
    </div>
  );
};

export default ProjectsPage;
